import React, {
  ComponentProps,
  Dispatch,
  memo,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import LoadingOutlined from '@ant-design/icons/lib/icons/LoadingOutlined';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import c from 'classnames';
import { LexicalEditor as LexicalEditorType } from 'lexical';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { v4 as uuid } from 'uuid';
import {
  ContactInteractionChannelSlug,
  ConversationItemUnion,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { LggButton } from 'src/components/general/button/lgg-button';
import { Scrollbar } from 'src/components/general/display/scrollbar';
import { Icon } from 'src/components/general/icon';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import {
  ContactInteractionsInputAreaAttachmentOptions,
  InputUploadFile,
} from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interaction-input-area-attachment-options';
import { MessageAttachments } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interactions-message-attachments';
import { Inv360WidgetPlugin } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/inv360-widget/inv360-widget';
import { ContactInteractionInputAreaMode } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/shared';
import { TemplateCustomPlaceholderResolver } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/placeholders-resolvers/template-custom-placeholder-resolver';
import { TemplatePresetPlaceholderResolver } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/placeholders-resolvers/template-preset-placeholder-resolver';
import { UserMentionNode } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/user-mention-node';
import { AiToolsPlugin } from 'src/components/pages/conversations/components/general/lexical-editor/plugins/ai-tools-picker-plugin';
import { InputAreaModeChangePlugin } from 'src/components/pages/conversations/components/general/lexical-editor/plugins/custom-clear-editor-plugin';
import { CustomSelectAllShortcutPlugin } from 'src/components/pages/conversations/components/general/lexical-editor/plugins/custom-select-all-shortcut-plugin';
import { DesktopSendKeyboardShortcutPlugin } from 'src/components/pages/conversations/components/general/lexical-editor/plugins/desktop-send-keyboard-shortcut-plugin';
import { PasteDnDFilePlugin } from 'src/components/pages/conversations/components/general/lexical-editor/plugins/dnd-paste-plugin';
import { EmojiPickerPlugin } from 'src/components/pages/conversations/components/general/lexical-editor/plugins/emoji-picker-plugin';
import { InsertPresetMessageTemplatePlaceholderPlugin } from 'src/components/pages/conversations/components/general/lexical-editor/plugins/insert-preset-message-template-placeholder-plugin';
import { MentionsPlugin } from 'src/components/pages/conversations/components/general/lexical-editor/plugins/mentions-plugin';
import { MessageTemplatesPlugin } from 'src/components/pages/conversations/components/general/lexical-editor/plugins/message-templates-plugin';
import { ReplyDetectorPlugin } from 'src/components/pages/conversations/components/general/lexical-editor/plugins/reply-detector-plugin';
import { LexicalContainer } from 'src/components/pages/conversations/components/general/lexical-editor/shared';
import { useBreakpoint } from 'src/hooks/use-breakpoint';

const LeftRail = styled(FlexRow)`
  align-items: center;

  & > *:not(:last-child) {
    margin-right: 20px;
  }
`;

const RightRail = styled(FlexRow)`
  max-height: 16px;
`;

const BottomBarContainer = styled(FlexRow)`
  align-items: center;
  display: none;
  justify-content: space-between;
  padding: 0 20px 7px;

  ${up('md')} {
    padding: 0 20px 12px;
  }

  &.visible {
    display: flex;
  }

  &.unfocused {
    bottom: 15px;
    height: 20px;
    min-height: 20px;
    padding: 0;
    position: absolute;
    right: 20px;

    ${LeftRail} {
      display: none;
    }
  }
`;

const SendIcon = styled(Icon)<{ $isActive: boolean }>`
  cursor: ${({ $isActive }) => ($isActive ? 'pointer' : 'not-allowed')};
  height: 20px;
  width: 20px;

  svg path {
    fill: ${({ theme, $isActive }) =>
      $isActive ? theme.colors.monk : theme.colors.casper};
  }
`;

const SendLoadingIcon = styled(LoadingOutlined)`
  font-size: 20px;
  height: 20px;
  width: 20px;

  svg {
    path {
      fill: ${({ theme }) => theme.colors.gogo};
    }
  }
`;

const LexicalEditorContainer = styled(FlexColumn)`
  flex: 1;
  max-height: 100%;
`;

const AddNoteButton = styled(LggButton)<{ $isActive: boolean }>`
  cursor: default;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  height: 30px;
  line-height: 18px;
  margin-top: -7px;
  padding: 6px 10px;

  &,
  &:focus,
  &:hover {
    background-color: ${({ theme }) => theme.colors.casper};
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      & {
        cursor: pointer;

        &,
        &:focus,
        &:hover {
          background-color: ${({ theme }) => theme.colors.secondaryGoldDark};
        }
      }
    `}
`;

type SendIconButtonProps = {
  enableSendButton: boolean;
  onClick: (editor: LexicalEditorType) => void;
  inputAreaMode: ContactInteractionInputAreaMode;
  isLoading?: boolean;
};

const SendMessageButton = memo<SendIconButtonProps>(
  ({ enableSendButton, onClick, inputAreaMode, isLoading = false }) => {
    const [editor] = useLexicalComposerContext();
    const { t } = useTranslation(['conversations']);

    const onClickHandler = useCallback(() => {
      if (isLoading || !enableSendButton) return;

      onClick(editor);
    }, [isLoading, enableSendButton, onClick, editor]);

    return inputAreaMode === ContactInteractionInputAreaMode.Reply ? (
      isLoading ? (
        <SendLoadingIcon />
      ) : (
        <SendIcon
          type="send"
          lggTestId="contact-interaction-message-submit-button"
          $isActive={enableSendButton}
          onClick={onClickHandler}
        />
      )
    ) : (
      <AddNoteButton
        type="button"
        data-lgg-id="contact-interaction-message-submit-button"
        $isActive={enableSendButton}
        onClick={onClickHandler}
        loading={isLoading}
      >
        {t('conversations:messageInput.modes.internalNote.addNote')}
      </AddNoteButton>
    );
  },
);

type PluginConfig = {
  visible: boolean;
  disabled?: boolean;
};

export type LexicalEditorProps = {
  placeholder: string;
  selectedChannel: ContactInteractionChannelSlug | null;
  onFocus?: VoidFunction;
  onChange: ComponentProps<typeof OnChangePlugin>['onChange'];
  hasFocus: boolean;
  isFullHeight: boolean;
  handleSendMessage: (editor: LexicalEditorType) => void;
  pluginConfig: {
    emojiPicker?: PluginConfig;
    messageAttachments?: PluginConfig;
    mentions?: PluginConfig;
    aiTools?: PluginConfig;
    messageTemplates?: PluginConfig;
    inv360?: PluginConfig;
  };
  enableSendButton: boolean;
  bottomContent: {
    leading?: ReactNode;
    trailing?: ReactNode;
    visible: boolean;
  };
  topContent?: ReactNode;
  attachmentList: InputUploadFile[];
  setAttachmentList: Dispatch<SetStateAction<InputUploadFile[]>>;
  resetEditorStateHandler: (editor: LexicalEditorType) => void;
  inputAreaMode: ContactInteractionInputAreaMode;
  setInputAreaMode: ValueChanged<ContactInteractionInputAreaMode>;
  setHasFocus: ValueChanged<boolean>;
  selectedInteraction?: ConversationItemUnion;
  isInputEnabled: boolean;
  rightRailLeadingContent?: ReactNode;
  isLoading?: boolean;
  showInput?: boolean;
};

const TextInputContainer = styled.div`
  padding-top: 15px;
  padding-bottom: 17px;
  max-height: 155px;
  height: max-content;

  .editor-input {
    max-height: 140px;
  }

  &.full-height {
    max-height: unset;
    flex: 1;

    .editor-input {
      flex: 1;
    }
  }

  ${up('md')} {
    padding-top: 19px;
    padding-bottom: 18px;
    max-height: unset;

    &.focused {
      padding-top: 15px;
    }
  }
`;

const lexicalEditorConfig = {
  namespace: 'LggEditor',
  theme: {
    ltr: 'ltr',
    rtl: 'rtl',
    placeholder: 'editor-placeholder',
    paragraph: 'editor-paragraph',
  },
  onError(error) {
    throw error;
  },
  nodes: [
    UserMentionNode,
    TemplateCustomPlaceholderResolver,
    TemplatePresetPlaceholderResolver,
  ],
};

export const LexicalEditor = memo<LexicalEditorProps>(
  ({
    placeholder,
    onFocus,
    onChange,
    bottomContent,
    hasFocus,
    selectedChannel = null,
    enableSendButton,
    handleSendMessage,
    pluginConfig,
    attachmentList,
    setAttachmentList,
    inputAreaMode,
    setInputAreaMode,
    resetEditorStateHandler,
    isFullHeight,
    topContent,
    isInputEnabled = false,
    setHasFocus,
    selectedInteraction,
    rightRailLeadingContent,
    isLoading = false,
    showInput = true,
  }) => {
    const breakpointUpMd = useBreakpoint(up('md'));
    const hasAttachments = attachmentList.length > 0;
    const editorContainerRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<HTMLDivElement>(null);
    const handleOnFocus = useCallback(() => {
      if (onFocus) {
        onFocus();
      }
    }, [onFocus]);

    const primaryPlugins = useMemo(
      () => (
        <>
          <OnChangePlugin onChange={onChange} />
          <ClearEditorPlugin />
          <InputAreaModeChangePlugin
            resetEditorStateHandler={resetEditorStateHandler}
            inputAreaMode={inputAreaMode}
            setHasFocus={setHasFocus}
          />
          <HistoryPlugin />
          <ReplyDetectorPlugin
            selectedInteraction={selectedInteraction}
            setHasFocus={setHasFocus}
            setInputAreaMode={setInputAreaMode}
            inputAreaMode={inputAreaMode}
          />
          <DesktopSendKeyboardShortcutPlugin onPressed={handleSendMessage} />
          <CustomSelectAllShortcutPlugin />
        </>
      ),
      [
        handleSendMessage,
        inputAreaMode,
        onChange,
        resetEditorStateHandler,
        selectedInteraction,
        setHasFocus,
        setInputAreaMode,
      ],
    );

    return (
      <LexicalEditorContainer ref={editorContainerRef}>
        <LexicalComposer initialConfig={lexicalEditorConfig}>
          {topContent}
          <LexicalContainer
            onFocus={handleOnFocus}
            className={c({
              'editor-container': true,
              'full-height': isFullHeight,
            })}
          >
            {isInputEnabled ? (
              <>
                {showInput ? (
                  <PlainTextPlugin
                    contentEditable={
                      <TextInputContainer
                        className={c({
                          focused: hasFocus,
                          'full-height': isFullHeight,
                        })}
                        ref={editorRef}
                        data-lgg-id="contact-interaction-message-input"
                      >
                        <Scrollbar>
                          <ContentEditable className="editor-input" />
                        </Scrollbar>
                      </TextInputContainer>
                    }
                    placeholder={
                      <TextInputContainer
                        className={c({
                          focused: hasFocus,
                          'editor-placeholder': true,
                        })}
                      >
                        {placeholder}
                      </TextInputContainer>
                    }
                    ErrorBoundary={LexicalErrorBoundary}
                  />
                ) : null}
                {primaryPlugins}
                {Boolean(pluginConfig.messageAttachments?.visible) && (
                  <>
                    <PasteDnDFilePlugin
                      onFileDropped={(files) => {
                        const filesWithId = files.map((file) => ({
                          ...file,
                          id: uuid(),
                          isRecentFile: false,
                        }));
                        setAttachmentList([...attachmentList, ...filesWithId]);
                      }}
                    />
                    {hasAttachments && selectedChannel && (
                      <MessageAttachments
                        attachments={attachmentList}
                        selectedChannel={selectedChannel}
                        setAttachmentList={setAttachmentList}
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              primaryPlugins
            )}
            {isInputEnabled && (
              <BottomBarContainer
                className={c({
                  unfocused: !hasFocus && isInputEnabled,
                  visible: bottomContent.visible,
                })}
              >
                <LeftRail>
                  {bottomContent?.leading && bottomContent.leading}
                  {isInputEnabled ? (
                    <>
                      {breakpointUpMd && pluginConfig.emojiPicker?.visible && (
                        <EmojiPickerPlugin
                          disabled={pluginConfig.emojiPicker?.disabled}
                        />
                      )}
                      {Boolean(pluginConfig.messageAttachments?.visible) && (
                        <ContactInteractionsInputAreaAttachmentOptions
                          setAttachmentList={setAttachmentList}
                          disabled={pluginConfig.messageAttachments?.disabled}
                        />
                      )}
                      {Boolean(pluginConfig.mentions?.visible) && (
                        <MentionsPlugin
                          editorContainerRef={editorContainerRef}
                          editorRef={editorRef}
                          disabled={pluginConfig.mentions?.disabled}
                        />
                      )}
                      {Boolean(pluginConfig.messageTemplates?.visible) && (
                        <>
                          <MessageTemplatesPlugin
                            disabled={pluginConfig.messageTemplates?.disabled}
                          />
                          <InsertPresetMessageTemplatePlaceholderPlugin
                            disabled={pluginConfig.messageTemplates?.disabled}
                          />
                        </>
                      )}
                      {Boolean(pluginConfig.aiTools?.visible) && (
                        <AiToolsPlugin disabled={pluginConfig.aiTools?.disabled} />
                      )}
                      {Boolean(pluginConfig.inv360?.visible) && (
                        <Inv360WidgetPlugin disabled={pluginConfig.inv360?.disabled} />
                      )}
                      {bottomContent?.trailing && bottomContent.trailing}
                    </>
                  ) : null}
                </LeftRail>
                <RightRail>
                  {isInputEnabled ? (
                    <>
                      {Boolean(rightRailLeadingContent) && rightRailLeadingContent}
                      <SendMessageButton
                        enableSendButton={enableSendButton}
                        onClick={handleSendMessage}
                        inputAreaMode={inputAreaMode}
                        isLoading={isLoading}
                      />
                    </>
                  ) : null}
                </RightRail>
              </BottomBarContainer>
            )}
          </LexicalContainer>
        </LexicalComposer>
      </LexicalEditorContainer>
    );
  },
);
